<template>
  <v-container fluid class="operation-logs">
    <v-card elevation="4" class="mx-auto logs-card">
      <v-card-title>{{ $t('operation_logs') }}</v-card-title>

      <v-card-text class="logs-content">
        <v-row dense class="filters-row">
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.tenantId"
              :items="tenantOptions"
              :label="$t('tenant')"
              item-text="title"
              item-value="tenant_id"
              :loading="loadingTenants"
              :disabled="loadingTenants"
              required
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="filters.dateStart" :label="$t('start_date')" type="datetime-local" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="filters.dateEnd" :label="$t('end_date')" type="datetime-local" hide-details />
          </v-col>
        </v-row>

        <v-row dense class="filters-row">
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="filters.eventKey" :label="$t('event_key')" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field v-model="filters.objectType" :label="$t('object_type')" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="filters.objectId" :label="$t('object_id')" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="filters.usersId" :label="$t('user_id')" hide-details />
          </v-col>
        </v-row>

        <v-row dense class="filters-row">
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="filters.userName" :label="$t('user_name')" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="filters.module" :label="$t('module')" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="d-flex align-center">
            <v-btn color="primary" :loading="loading" :disabled="!filters.tenantId" block @click="search">
              <v-icon left>mdi-magnify</v-icon>{{ $t('search') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <div class="results-header">{{ $t('operation_logs') }}</div>
        <div class="results-list">
          <v-progress-linear v-if="loading" indeterminate color="secondary" />
          <v-list v-else-if="hasSearched && filteredLogs.length" two-line>
            <v-list-item v-for="log in filteredLogs" :key="log._id || log.id" @click="openDetails(log)">
              <v-list-item-content>
                <v-list-item-title>{{ interpolateText(log) }}</v-list-item-title>
                <v-list-item-subtitle>{{ logSummary(log) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else-if="hasSearched" class="empty-results">{{ $t('no_logs_found') }}</div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="detailsOpen" max-width="760px">
      <v-card v-if="selectedLog">
        <v-card-title>{{ $t('log_details') }}</v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-6">{{ interpolateText(selectedLog) }}</p>
          <v-simple-table>
            <tbody>
              <tr v-for="field in detailFields" :key="field.key">
                <td class="font-weight-medium">{{ field.label }}</td>
                <td>{{ field.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-if="selectedLog.context" class="mt-5">
            <div class="font-weight-medium mb-2">{{ $t('context') }}</div>
            <pre class="context-value">{{ formattedContext }}</pre>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="secondary" @click="detailsOpen = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '@/services/api'

const dateForInput = (endOfDay) => {
  const now = new Date()
  const date = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-')
  return `${date}T${endOfDay ? '23:59' : '00:00'}`
}

export default {
  name: 'OperationLogs',

  data() {
    return {
      filters: {
        tenantId: null,
        dateStart: dateForInput(false),
        dateEnd: dateForInput(true),
        eventKey: '',
        objectType: '',
        objectId: '',
        usersId: '',
        userName: '',
        module: '',
      },
      tenants: [],
      loadingTenants: false,
      logs: [],
      loading: false,
      hasSearched: false,
      detailsOpen: false,
      selectedLog: null,
    }
  },

  computed: {
    filteredLogs() {
      const module = this.filters.module.trim().toLowerCase()
      return this.logs.filter(log => !module || String(log.app_key || '').toLowerCase().includes(module))
    },

    tenantOptions() {
      const admins = this.$store.state.core.auth.userData.admins || []
      const tenantIds = admins.map(admin => admin.tenant_id)
      return this.tenants.filter(tenant => tenantIds.includes(tenant.tenant_id))
    },

    detailFields() {
      if (!this.selectedLog) return []
      return [
        { key: 'event_key', label: this.$t('event'), value: this.selectedLog.event_key || '-' },
        { key: 'object_type', label: this.$t('object_type'), value: this.selectedLog.object_type || '-' },
        { key: 'object_id', label: this.$t('object_id'), value: this.selectedLog.object_id || '-' },
        { key: 'tenant_id', label: this.$t('tenant'), value: this.selectedLog.tenant_id || '-' },
        { key: 'user_name', label: this.$t('user'), value: this.selectedLog.user_name || '-' },
        { key: 'date', label: this.$t('date'), value: this.selectedLog.date || '-' },
        { key: 'app_key', label: this.$t('module'), value: this.selectedLog.app_key || '-' },
      ]
    },

    formattedContext() {
      return JSON.stringify(this.selectedLog.context, null, 2)
    },
  },

  mounted() {
    this.loadTenants()
  },

  methods: {
    queryDate(value) {
      return value ? `${value.replace('T', ' ')}:00` : undefined
    },

    extractLogs(data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data.items)) return data.items
      if (data.data) return this.extractLogs(data.data)
      if (data.hits && Array.isArray(data.hits.hits)) return data.hits.hits.map(hit => ({ ...hit._source, _id: hit._id }))
      if (Array.isArray(data.hits)) return data.hits
      return []
    },

    async loadTenants() {
      this.loadingTenants = true
      try {
        const response = await api.core.tenant.getAllTenants()
        this.tenants = response.data.items || response.data.data || response.data || []
      } finally {
        this.loadingTenants = false
      }
    },

    async search() {
      this.loading = true
      this.hasSearched = true
      try {
        const response = await api.node.history.search({
          tenant_id: this.filters.tenantId,
          date_start: this.queryDate(this.filters.dateStart),
          date_end: this.queryDate(this.filters.dateEnd),
          event_key: this.filters.eventKey || undefined,
          object_type: this.filters.objectType || undefined,
          object_id: this.filters.objectId || undefined,
          users_id: this.filters.usersId || undefined,
          user_name: this.filters.userName || undefined,
        })
        this.logs = this.extractLogs(response.data).map(log => log._source ? { ...log._source, _id: log._id } : log)
      } catch (error) {
        this.logs = []
      } finally {
        this.loading = false
      }
    },

    openDetails(log) {
      this.selectedLog = log
      this.detailsOpen = true
    },

    logSummary(log) {
      return `${this.$t('event')}: ${log.event_key || '-'}, ${log.object_type || '-'}: ${log.object_id || '-'}, ${this.$t('tenant')}: ${log.tenant_id || '-'}, ${this.$t('user')}: ${log.user_name || '-'}, ${this.$t('date')}: ${log.date || '-'}`
    },

    interpolateText(log) {
      if (!log.text) return ''
      const values = { ...log, ...(log.context || {}) }
      return log.text.replace(/\{([^}]+)\}/g, (placeholder, key) => {
        const value = values[key]
        if (value === undefined || value === null) return placeholder
        return key === 'date' && typeof value === 'string' ? value.replace(/\+0000$/, '') : value
      })
    },
  },
}
</script>

<style scoped>
.operation-logs {
  display: flex;
  height: calc(100vh - 64px);
  min-height: 520px;
}

.logs-card {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.logs-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

.filters-row {
  flex: 0 0 auto;
}

.results-header { font-size: 1.1rem; font-weight: 600; }
.results-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  margin-top: 12px;
}
.empty-results { color: rgba(0, 0, 0, 0.6); padding: 24px; text-align: center; }
.context-value { background: rgba(0, 0, 0, 0.05); max-height: 240px; overflow: auto; padding: 12px; white-space: pre-wrap; }

@media (max-width: 959px) {
  .operation-logs {
    display: block;
    height: auto;
    min-height: 0;
  }

  .results-list {
    height: 460px;
  }
}
</style>
